import { Theme, ThemedText } from '@app/theme';
import { useTheme } from '@shopify/restyle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  LayoutChangeEvent,
  PanResponder,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { LimitConfig } from '../../types';
import {
  formatSliderLabel,
  getAmountFromPosition,
  getPositionFromAmount,
  getThumbMaxPosition,
  getThumbMinPosition,
  RATE_ZONE_HEIGHT,
  SLIDER_BLOCK_HEIGHT,
  SLIDER_HEIGHT,
  THUMB_BORDER_RADIUS,
  THUMB_GRIP_LINE_COLOR,
  THUMB_SIZE,
  THUMB_TOP,
  toFadeGradient,
  TRACK_HEIGHT,
  TRACK_TOP,
} from './sliderUtils';

const NUDGE_OFFSET = 8;
const NUDGE_STEP_MS = 160;
const NUDGE_DELAY_MS = 280;

type LimitSliderProps = {
  limitConfig: LimitConfig;
  selectedAmount: number;
  onAmountChange: (amount: number) => void;
  showThumbGrip?: boolean;
  showRateZones?: boolean;
  /**
   * Play a one-time wiggle of the thumb (around its current position) shortly
   * after mount to hint that the slider is draggable. Skipped once the user
   * interacts.
   */
  enableNudge?: boolean;

  onSlideComplete?: () => void;
};

const LimitSlider = ({
  limitConfig,
  selectedAmount,
  onAmountChange,
  showThumbGrip = true,
  showRateZones = true,
  enableNudge = false,
  onSlideComplete,
}: LimitSliderProps): React.ReactElement => {
  const { colors } = useTheme<Theme>();
  const [trackWidth, setTrackWidth] = useState(0);

  const sliderLabels = useMemo(
    () => [limitConfig.minAmount, limitConfig.maxAmount],
    [limitConfig],
  );

  const isHighRate =
    limitConfig.hasRateThreshold && selectedAmount > limitConfig.rateThreshold;
  const currentRate = isHighRate ? limitConfig.highRate : limitConfig.lowRate;

  const translateX = useRef(new Animated.Value(0)).current;
  const thumbHalfOffset = useRef(new Animated.Value(THUMB_SIZE / 2)).current;
  const dragStartPosition = useRef(0);
  const isDragging = useRef(false);
  const hasUserInteracted = useRef(false);
  const hasPlayedNudge = useRef(false);
  const isNudgeAnimating = useRef(false);
  const hasInitialPositioned = useRef(false);
  const limitConfigRef = useRef(limitConfig);
  const trackWidthRef = useRef(trackWidth);
  const onAmountChangeRef = useRef(onAmountChange);
  const onSlideCompleteRef = useRef(onSlideComplete);

  limitConfigRef.current = limitConfig;
  trackWidthRef.current = trackWidth;
  onAmountChangeRef.current = onAmountChange;
  onSlideCompleteRef.current = onSlideComplete;

  const sliderBlockHeight = showRateZones ? SLIDER_BLOCK_HEIGHT : SLIDER_HEIGHT;

  const onTrackLayout = useCallback((e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width;
    if (width > 0) {
      setTrackWidth(width);
    }
  }, []);

  const sliderStyles = useMemo(() => {
    const androidNoLift = Platform.OS === 'android' ? { elevation: 0 } : {};

    const thumbFlat = {
      elevation: 0,
      shadowOpacity: 0,
      shadowRadius: 0,
      shadowOffset: { width: 0, height: 0 },
    };

    return StyleSheet.create({
      trackContainer: {
        width: '100%',
        overflow: 'visible',
      },
      sliderBlock: {
        position: 'relative',
        height: SLIDER_BLOCK_HEIGHT,
        width: '100%',
        overflow: 'visible',
      },
      sliderWrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: SLIDER_HEIGHT,
        zIndex: 10,
        ...androidNoLift,
      },
      sliderTouchLayer: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 12,
      },
      trackBg: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: TRACK_TOP,
        height: TRACK_HEIGHT,
        backgroundColor: colors['border-form'],
        borderRadius: 4,
        zIndex: 10,
        ...androidNoLift,
      },
      trackFill: {
        position: 'absolute',
        left: 0,
        top: TRACK_TOP,
        height: TRACK_HEIGHT,
        backgroundColor: colors['bg-action-primary'],
        borderRadius: 4,
        zIndex: 11,
        ...androidNoLift,
      },
      thumb: {
        position: 'absolute',
        top: THUMB_TOP,
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        backgroundColor: colors['bg-surface-primary'],
        borderRadius: THUMB_BORDER_RADIUS,
        borderWidth: 1,
        borderColor: colors['border-primary'],
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 13,
        ...thumbFlat,
      },
      thumbLine: {
        width: 2,
        height: 12,
        backgroundColor: THUMB_GRIP_LINE_COLOR,
        borderRadius: 4,
        marginHorizontal: 1,
      },
      labelRow: {
        position: 'relative',
        marginBottom: 8,
      },
      labelAtPosition: {
        position: 'absolute',
        top: 0,
      },
      zonesRow: {
        position: 'absolute',
        top: TRACK_TOP,
        left: 0,
        flexDirection: 'row',
        height: RATE_ZONE_HEIGHT,
        zIndex: 0,
      },
      rateZone: {
        height: RATE_ZONE_HEIGHT,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: TRACK_HEIGHT + 14,
        paddingBottom: 12,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
      },
    });
  }, [colors]);

  const activeZoneGradient = useMemo(
    () => toFadeGradient(colors['bg-surface-emphasis-primary']),
    [colors],
  );

  useEffect(() => {
    if (trackWidth <= 0 || isDragging.current) {
      return;
    }

    if (limitConfig.maxAmount <= 0) {
      return;
    }

    translateX.stopAnimation();
    if (isNudgeAnimating.current) {
      isNudgeAnimating.current = false;
    }

    const targetPosition = getPositionFromAmount(
      selectedAmount,
      limitConfig,
      trackWidth,
    );

    if (!hasInitialPositioned.current) {
      hasInitialPositioned.current = true;
      translateX.setValue(targetPosition);
      return;
    }

    Animated.spring(translateX, {
      toValue: targetPosition,
      useNativeDriver: false,
      friction: 8,
    }).start();
  }, [selectedAmount, limitConfig, trackWidth, translateX]);


  useEffect(() => {
    if (!enableNudge) {
      return;
    }
    if (
      trackWidth <= 0 ||
      limitConfig.maxAmount <= 0 ||
      hasUserInteracted.current ||
      hasPlayedNudge.current
    ) {
      return;
    }

    const basePosition = getPositionFromAmount(
      selectedAmount,
      limitConfig,
      trackWidth,
    );

    translateX.setValue(basePosition);
    hasPlayedNudge.current = true;
    isNudgeAnimating.current = true;

    const minPos = getThumbMinPosition();
    const maxPos = getThumbMaxPosition(trackWidth);
    const nudgeLeft = Math.max(minPos, basePosition - NUDGE_OFFSET);
    const nudgeRight = Math.min(maxPos, basePosition + NUDGE_OFFSET);

    const nudgeStep = (toValue: number) =>
      Animated.timing(translateX, {
        toValue,
        duration: NUDGE_STEP_MS,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: false,
      });

    const timer = setTimeout(() => {
      Animated.sequence([
        nudgeStep(nudgeLeft),
        nudgeStep(nudgeRight),
        nudgeStep(nudgeLeft),
        nudgeStep(nudgeRight),
        nudgeStep(basePosition),
      ]).start(() => {
        isNudgeAnimating.current = false;
      });
    }, NUDGE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [trackWidth, limitConfig, selectedAmount, translateX, enableNudge]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => trackWidthRef.current > 0,
      onStartShouldSetPanResponderCapture: () => trackWidthRef.current > 0,
      onMoveShouldSetPanResponder: (_, gesture) =>
        trackWidthRef.current > 0 &&
        Math.abs(gesture.dx) > Math.abs(gesture.dy),
      onMoveShouldSetPanResponderCapture: (_, gesture) =>
        trackWidthRef.current > 0 &&
        Math.abs(gesture.dx) > Math.abs(gesture.dy),
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: (evt) => {
        hasUserInteracted.current = true;
        isNudgeAnimating.current = false;
        translateX.stopAnimation();
        isDragging.current = true;
        const tw = trackWidthRef.current;
        const config = limitConfigRef.current;
        const minPos = getThumbMinPosition();
        const maxPos = getThumbMaxPosition(tw);
        if (maxPos <= minPos) return;

        const tapX = evt.nativeEvent.locationX - THUMB_SIZE / 2;
        const newPosition = Math.max(minPos, Math.min(maxPos, tapX));
        dragStartPosition.current = newPosition;
        translateX.setValue(newPosition);
        onAmountChangeRef.current(
          getAmountFromPosition(newPosition, config, tw),
        );
      },
      onPanResponderMove: (_, gesture) => {
        const tw = trackWidthRef.current;
        const config = limitConfigRef.current;
        const minPos = getThumbMinPosition();
        const maxPos = getThumbMaxPosition(tw);
        if (maxPos <= minPos) return;

        let newPosition = dragStartPosition.current + gesture.dx;
        newPosition = Math.max(minPos, Math.min(maxPos, newPosition));
        translateX.setValue(newPosition);
        onAmountChangeRef.current(
          getAmountFromPosition(newPosition, config, tw),
        );
      },
      onPanResponderRelease: () => {
        isDragging.current = false;
        onSlideCompleteRef.current?.();
      },
      onPanResponderTerminate: () => {
        isDragging.current = false;
      },
    }),
  ).current;

  const renderLabelAtAmount = (amount: number, index: number) => {
    const isFirst = index === 0;
    const isLast = index === sliderLabels.length - 1;
    const labelMaxWidth = Math.max(72, Math.min(160, trackWidth * 0.34));

    return (
      <View
        key={amount}
        style={[
          sliderStyles.labelAtPosition,
          {
            left: isFirst ? 0 : undefined,
            right: isLast ? 0 : undefined,
            maxWidth: labelMaxWidth,
          },
        ]}
      >
        <ThemedText
          variant="b5m"
          color="text-primary"
          numberOfLines={1}
          adjustsFontSizeToFit
          minimumFontScale={0.55}
          style={{
            textAlign: isFirst ? 'left' : 'right',
          }}
        >
          {formatSliderLabel(amount, isLast)}
        </ThemedText>
      </View>
    );
  };

  // Single full-width zone showing the active rate for the current amount.
  const renderRateZonesBelowTrack = () => (
    <View style={[sliderStyles.zonesRow, { width: trackWidth }]}>
      <LinearGradient
        colors={[...activeZoneGradient]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={[sliderStyles.rateZone, { width: trackWidth }]}
      >
        <ThemedText variant="b5m" color="text-action-primary">
          INTEREST
        </ThemedText>
        <ThemedText variant="b3s" color="text-action-primary">
          {currentRate}% p.a.
        </ThemedText>
      </LinearGradient>
    </View>
  );

  return (
    <>
      {trackWidth > 0 ? (
        <View style={sliderStyles.labelRow}>
          {sliderLabels.map(renderLabelAtAmount)}
        </View>
      ) : (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          {sliderLabels.map((label, index) => (
            <ThemedText key={label} variant="b5m" color="text-secondary">
              {formatSliderLabel(label, index === sliderLabels.length - 1)}
            </ThemedText>
          ))}
        </View>
      )}

      <View style={sliderStyles.trackContainer} onLayout={onTrackLayout}>
        {trackWidth > 0 && (
          <View
            style={[
              sliderStyles.sliderBlock,
              { width: trackWidth, alignSelf: 'center', height: sliderBlockHeight },
            ]}
          >
            {showRateZones ? renderRateZonesBelowTrack() : null}
            <View style={sliderStyles.sliderWrapper}>
              <View style={sliderStyles.trackBg} />
              <Animated.View
                style={[
                  sliderStyles.trackFill,
                  {
                    width: Animated.add(translateX, thumbHalfOffset),
                  },
                ]}
                pointerEvents="none"
              />
              <Animated.View
                pointerEvents="none"
                style={[
                  sliderStyles.thumb,
                  { transform: [{ translateX }] },
                ]}
              >
                {showThumbGrip ? (
                  <>
                    <View style={sliderStyles.thumbLine} />
                    <View style={sliderStyles.thumbLine} />
                    <View style={sliderStyles.thumbLine} />
                  </>
                ) : null}
              </Animated.View>
              <View
                {...panResponder.panHandlers}
                style={sliderStyles.sliderTouchLayer}
              />
            </View>
          </View>
        )}
      </View>
    </>
  );
};

export default LimitSlider;
